import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  initializeStore,
  purchaseProduct,
  restorePurchases as restorePurchasesPlugin,
  isIOS,
  isStoreReady,
  onStoreReady,
  getApplePrice,
} from '../utils/payments';
import {
  useProfile,
  useSubscriptionStatus,
  useTrackUsage,
  useRefreshSubscription,
} from '../hooks/useProfile';
import useAuthStore from '../stores/useAuthStore';

const PREMIUM_FEATURES = {
  areas: true,
  unlimitedProjects: true,
  repeatingTasks: true,
  advancedSearch: true,
};

const FREE_PROJECT_LIMIT = 5;

export function SubscriptionProvider({ children }) {
  // Provider only orchestrates the IAP store init — all data lives in React Query.
  const refresh = useRefreshSubscription();
  const iapInitialized = useRef(false);

  useEffect(() => {
    if (!isIOS() || iapInitialized.current) return;
    iapInitialized.current = true;
    initializeStore(() => refresh()).then(() => {
      // store ready callback — React Query handles the data refetch
    });
  }, [refresh]);

  return <>{children}</>;
}

export function useSubscription() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const profileQuery = useProfile();
  const statusQuery = useSubscriptionStatus();
  const trackUsageMutation = useTrackUsage();
  const refresh = useRefreshSubscription();

  // `isStoreReady()` is a module-level flag — reading it during render alone
  // would latch whatever it was on first paint and never update, leaving any
  // control gated on it stuck in its pre-ready state for the app's lifetime.
  const [iapReady, setIapReady] = useState(() => isStoreReady());
  useEffect(() => {
    if (!isIOS() || iapReady) return undefined;
    let cancelled = false;
    onStoreReady(() => {
      if (!cancelled) setIapReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [iapReady]);

  const profile = profileQuery.data || null;
  const status = statusQuery.data || null;
  const loading = profileQuery.isLoading || statusQuery.isLoading;

  const isPremium = status?.isPremium ?? false;
  const isTrialing = status?.isTrialing ?? false;
  const trialDaysRemaining = status?.daysRemaining ?? 0;
  const isTrialExpired =
    profile?.subscriptionStatus === 'free' && profile?.trialStartedAt != null;

  const canUseFeature = useCallback(
    (featureName) => {
      if (!PREMIUM_FEATURES[featureName]) return true;
      return isPremium;
    },
    [isPremium]
  );

  const canCreateProject = useCallback(
    (currentCount) => {
      if (isPremium) return true;
      return currentCount < FREE_PROJECT_LIMIT;
    },
    [isPremium]
  );

  const startApplePurchase = useCallback(async (planId) => {
    await purchaseProduct(planId);
    let retries = 0;
    const poll = async () => {
      retries++;
      refresh();
      if (retries < 5) setTimeout(poll, 2000);
    };
    setTimeout(poll, 1500);
  }, [refresh]);

  const trackUsage = useCallback(
    (metric) => {
      if (!isAuthenticated) return;
      trackUsageMutation.mutate(metric);
    },
    [isAuthenticated, trackUsageMutation]
  );

  const getPaymentPlatform = useCallback(() => {
    // Apple IAP is the only payment path. Native → 'apple'; web has no in-app upgrade.
    if (window.Capacitor?.isNativePlatform?.()) return 'apple';
    return 'web';
  }, []);

  const refreshProfile = useCallback(async () => {
    refresh();
  }, [refresh]);

  return {
    profile,
    status,
    loading,
    isPremium,
    isTrialing,
    trialDaysRemaining,
    isTrialExpired,
    canUseFeature,
    canCreateProject,
    startApplePurchase,
    trackUsage,
    getPaymentPlatform,
    getApplePrice,
    iapReady,
    restorePurchases: restorePurchasesPlugin,
    refreshProfile,
  };
}
